import { useId, type ReactNode } from 'react';
import { SelectionIndicator } from './SelectionIndicator';

export interface TabsProps<T extends string> {
  items: Array<{ id: T; label: ReactNode; disabled?: boolean }>;
  active: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
}

export function Tabs<T extends string>({ items, active, onChange, ariaLabel }: TabsProps<T>) {
  const indicator = useId();
  return <div className="fd-tabs fd-tabs--motion" role="tablist" aria-label={ariaLabel}>
    {items.map(item => <button key={item.id} role="tab" type="button" disabled={item.disabled}
      aria-selected={active === item.id} tabIndex={active === item.id || (!items.some(i => i.id === active && !i.disabled) && items.find(i => !i.disabled)?.id === item.id) ? 0 : -1} className="fd-tab"
      onClick={() => onChange(item.id)}
      onKeyDown={event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const enabled = items.filter(i => !i.disabled), index = enabled.findIndex(i => i.id === item.id);
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
        const next = enabled[target];
        if (!next) return;
        const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        buttons?.[target]?.focus(); onChange(next.id);
      }}>
      {active === item.id && <SelectionIndicator id={indicator} />}
      <span className="fd-choice-label">{item.label}</span>
    </button>)}
  </div>;
}
